// Copyright 2020 TOKU Systems Inc.
import './app-bar-with-menu.css';
import React, { useState } from 'react';
import {
  AppBar,
  Button,
  Avatar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
  Container,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import { useTheme } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import illumass from '../../services/illumass';
import { useDispatch, useSelector } from 'react-redux';
import { RarityAppState } from '../../store';
import _ from 'lodash';
import SwitchTenantDialog from './switch-tenant-dialog';

interface AppBarWithMenuProps {
  title: string;
  showGoBack?: boolean;
}

export default function AppBarWithMenu(
  props: AppBarWithMenuProps
): JSX.Element {
  const [openMenu, setOpenMenu] = useState(false);
  const [openSwitchTenant, setOpenSwitchTenant] = useState(false);
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { user, tenant } = useSelector<
    RarityAppState,
    {
      user: { id: string; profileIds: string[]; name: string; email: string };
      tenant: { id: string; name: string };
    }
  >((state) => ({
    user: _.get(state, ['login']) ?? {
      id: '',
      profileIds: [] as string[],
      name: '',
      email: '',
    },
    tenant: _.get(state, ['tenant']) ?? { id: '', name: '' },
  }));

  const hasSystemScope = !_.isEmpty(
    _.intersection(['admin', 'support', 'auditor'], user.profileIds)
  );

  const userNameParts: string[] = user.name.split(/\s+/);
  const initials = _.filter(
    _.map(userNameParts, (part) => part.charAt(0).toUpperCase().trim()),
    (initial) => initial.length > 0
  );
  const avatarInitials = _.join(initials, '');
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<HTMLElement | null>(
    null
  );

  const handleDrawerOpen = (): void => setOpenMenu(true);
  const handleDrawerClose = (): void => setOpenMenu(false);
  const handleSignOutClick = (): void => {
    setAvatarMenuAnchor(null);
    illumass.auth.logout().then(() => {
      dispatch({ type: 'logout' });
      window.location.reload();
    });
  };
  const handleApiKeysClick = (): void => {
    setOpenMenu(false);
    history.push('/api-keys');
  };
  const handleDevicesClick = (): void => {
    setOpenMenu(false);
    history.push('/devices');
  };
  const handleAssetsClick = (): void => {
    setOpenMenu(false);
    history.push('/assets');
  };
  const handleHardpointsClick = (): void => {
    setOpenMenu(false);
    history.push('/hardpoints');
  };

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleAvatarClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleAvatarMenuClose = (): void => {
    setAvatarMenuAnchor(null);
  };

  const handleSwitchCustomerClick = (): void => {
    setOpenSwitchTenant(true);
  };

  const handleSwitchTenantClose = (switchedTenant: boolean): void => {
    setAvatarMenuAnchor(null);
    setOpenSwitchTenant(false);
    if (switchedTenant) {
      history.go(0);
    }
  };

  const leftButton: JSX.Element = props.showGoBack ? (
    <IconButton
      edge="start"
      onClick={handleBackClick}
      color="inherit"
      aria-label="menu"
    >
      <Icon>arrow_back</Icon>
    </IconButton>
  ) : (
    <IconButton
      edge="start"
      onClick={handleDrawerOpen}
      color="inherit"
      aria-label="menu"
      className="stack-menu"
    >
      <Icon>menu</Icon>
    </IconButton>
  );

  return (
    <React.Fragment>
      <Drawer anchor="left" open={openMenu} onClose={handleDrawerClose}>
        <List>
          <ListItem button onClick={handleAssetsClick}>
            <ListItemIcon>
              <Icon>account_tree</Icon>
            </ListItemIcon>
            <ListItemText>Assets</ListItemText>
          </ListItem>
          <ListItem button onClick={handleHardpointsClick}>
            <ListItemIcon>
              <Icon>speed</Icon>
            </ListItemIcon>
            <ListItemText>Hardpoints</ListItemText>
          </ListItem>
          <ListItem button onClick={handleDevicesClick}>
            <ListItemIcon>
              <Icon>router</Icon>
            </ListItemIcon>
            <ListItemText>Devices</ListItemText>
          </ListItem>
        </List>
        <Divider />
        <List subheader={<ListSubheader>Settings</ListSubheader>}>
          <ListItem button onClick={handleApiKeysClick}>
            <ListItemIcon>
              <Icon>vpn_key</Icon>
            </ListItemIcon>
            <ListItemText>Manage API keys</ListItemText>
          </ListItem>
        </List>
      </Drawer>
      <AppBar position="static" style={{ marginBottom: theme.spacing(2) }}>
        <Toolbar>
          {leftButton}
          <Typography variant="h6" className="app-title">
            {props.title}
          </Typography>
          {avatarInitials !== undefined && (
            <Button
              color="inherit"
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleAvatarClick}
            >
              <Avatar>
                {avatarInitials !== '' ? avatarInitials : <Icon>person</Icon>}
              </Avatar>
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Menu
        open={avatarMenuAnchor !== null}
        anchorEl={avatarMenuAnchor}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleAvatarMenuClose}
      >
        <MenuItem>
          <Container className="user-info">
            <Typography variant="body1">
              {user.name.trim().length > 0 ? user.name.trim() : 'N/A'}
            </Typography>
            <Typography variant="body2">
              {user.email ?? 'No email address'}
            </Typography>
            <Typography variant="body2">{tenant.name ?? 'N/A'}</Typography>
          </Container>
        </MenuItem>
        <Divider />
        {hasSystemScope && (
          <MenuItem onClick={handleSwitchCustomerClick}>
            Switch customer
          </MenuItem>
        )}
        <MenuItem onClick={handleSignOutClick}>Sign out</MenuItem>
      </Menu>
      {hasSystemScope && (
        <SwitchTenantDialog
          open={openSwitchTenant}
          onClose={handleSwitchTenantClose}
        />
      )}
    </React.Fragment>
  );
}
